import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete, Schedule } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { TableShimmer } from '../../components/common/Shimmer';
import shiftService, { Shift } from '../../services/shiftService';
import clusterService from '../../services/clusterService';
import { Cluster } from '../../types';
import { SHIFT_TYPE_OPTIONS } from '../../constants';
import RadialTimePicker from '../../components/shifts/RadialTimePicker';

const shiftSchema = yup.object({
  name: yup.string().required('Shift name is required'),
  type: yup.string().required('Shift type is required'),
  start_time: yup.string().when('type', {
    is: (type: string) => type === 'time_based',
    then: () => yup.string().required('Start time is required'),
    otherwise: () => yup.string().optional(),
  }),
  end_time: yup.string().when('type', {
    is: (type: string) => type === 'time_based',
    then: () => yup.string().required('End time is required'),
    otherwise: () => yup.string().optional(),
  }),
  description: yup.string().optional(),
  cluster_id: yup.string().optional(),
  is_active: yup.boolean().optional(),
});

const ShiftsPage: React.FC = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [deletingShift, setDeletingShift] = useState<Shift | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Shift>({
    resolver: yupResolver(shiftSchema) as any,
    defaultValues: {
      is_active: true,
    },
  });

  const shiftType = watch('type');

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const response = await shiftService.getShifts({ limit: 100 });
      setShifts(response.shifts);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch shifts');
      showSnackbar('Failed to fetch shifts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      const response = await clusterService.getClusters();
      setClusters(response.clusters);
    } catch (err: any) {
      console.error('Failed to fetch clusters:', err);
    }
  };

  useEffect(() => {
    fetchShifts();
    fetchClusters();
  }, []);

  const handleCreateShift = async (data: Partial<Shift>) => {
    try {
      const response = await shiftService.createShift(data);
      setShifts([...shifts, response]);
      setOpenDialog(false);
      reset();
      setError(null);
      showSnackbar('Shift created successfully!', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create shift';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleUpdateShift = async (data: Partial<Shift>) => {
    if (!editingShift) return;
    try {
      const response = await shiftService.updateShift(editingShift.id, data);
      setShifts(shifts.map(s => s.id === editingShift.id ? response : s));
      setOpenDialog(false);
      setEditingShift(null);
      reset();
      setError(null);
      showSnackbar('Shift updated successfully!', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update shift';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteShift = async () => {
    if (!deletingShift) return;
    try {
      await shiftService.deleteShift(deletingShift.id);
      setShifts(shifts.filter(s => s.id !== deletingShift.id));
      setDeletingShift(null);
      showSnackbar('Shift deleted successfully!', 'success');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete shift';
      showSnackbar(errorMessage, 'error');
      setDeletingShift(null);
    }
  };

  const handleEditClick = (shift: Shift) => {
    setEditingShift(shift);
    reset({
      name: shift.name,
      type: shift.type,
      start_time: shift.start_time || '',
      end_time: shift.end_time || '',
      description: shift.description || '',
      cluster_id: shift.cluster_id || '',
      is_active: shift.is_active,
    });
    setOpenDialog(true);
  };

  const handleCreateClick = () => {
    setEditingShift(null);
    reset({
      name: '',
      type: 'time_based',
      start_time: '',
      end_time: '',
      description: '',
      cluster_id: '',
      is_active: true,
    });
    setOpenDialog(true);
  };

  const getClusterName = (clusterId: string | undefined) => {
    if (!clusterId) return 'General';
    const cluster = clusters.find(c => c.id === clusterId);
    return cluster ? cluster.name : 'Unknown';
  };

  const getShiftTypeLabel = (type: string) => {
    const option = SHIFT_TYPE_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : type;
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Shift Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage work shifts and schedules
          </Typography>
        </Box>
        <TableShimmer rows={6} columns={7} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Shift Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Manage work shifts, schedules, and assignments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClick}
          sx={{
            background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
            },
            boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
            px: 3,
            py: 1.5,
          }}
        >
          Create Shift
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Time Range</TableCell>
              <TableCell>Cluster</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No shifts found. Create your first shift to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              shifts.map((shift) => (
                <TableRow key={shift.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {shift.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getShiftTypeLabel(shift.type)}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {shift.type === 'time_based' && shift.start_time && shift.end_time ? (
                      <Typography variant="body2">
                        {shift.start_time} - {shift.end_time}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getClusterName(shift.cluster_id)}
                      color="secondary"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={shift.is_active ? 'Active' : 'Inactive'}
                      color={shift.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(shift)}
                      title="Edit Shift"
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeletingShift(shift)}
                      title="Delete Shift"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Shift Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditingShift(null);
          reset();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Schedule sx={{ color: '#081122' }} />
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              {editingShift ? 'Edit Shift' : 'Create New Shift'}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(editingShift ? handleUpdateShift : handleCreateShift)}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  {...register('name')}
                  label="Shift Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder="e.g., Morning Shift"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Shift Type</InputLabel>
                  <Select
                    {...register('type')}
                    label="Shift Type"
                    value={watch('type') || ''}
                    onChange={(e) => setValue('type', e.target.value as any)}
                  >
                    {SHIFT_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.type.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {shiftType === 'time_based' && (
                <>
                  <Grid item xs={6}>
                    <RadialTimePicker
                      value={watch('start_time') || ''}
                      onChange={(time) => setValue('start_time', time)}
                      label="Start Time"
                      required
                      error={!!errors.start_time}
                      helperText={errors.start_time?.message}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <RadialTimePicker
                      value={watch('end_time') || ''}
                      onChange={(time) => setValue('end_time', time)}
                      label="End Time"
                      required
                      error={!!errors.end_time}
                      helperText={errors.end_time?.message}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Cluster (Optional)</InputLabel>
                  <Select
                    {...register('cluster_id')}
                    label="Cluster (Optional)"
                    value={watch('cluster_id') || ''}
                    onChange={(e) => setValue('cluster_id', e.target.value)}
                  >
                    <MenuItem value="">General (No Cluster)</MenuItem>
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name} ({cluster.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('description')}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  placeholder="Enter shift description..."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    {...register('is_active')}
                    label="Status"
                    value={watch('is_active') ? 'true' : 'false'}
                    onChange={(e) => setValue('is_active', e.target.value === 'true')}
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDialog(false);
              setEditingShift(null);
              reset();
            }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {editingShift ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingShift} onClose={() => setDeletingShift(null)}>
        <DialogTitle>Delete Shift</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete shift "{deletingShift?.name}"? 
            This action cannot be undone. Make sure no users are assigned to this shift.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingShift(null)}>Cancel</Button>
          <Button onClick={handleDeleteShift} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShiftsPage;

